import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SlidePagePanel from '../mobile/SlidePagePanel'
import CycleTrackingPanel, { CycleTrackingTeaser, type CyclePanelStep } from './CycleTrackingPanel'
import LanguagePicker from './LanguagePicker'
import CenteredMetricStepper from '../ui/CenteredMetricStepper'
import CenteredTimePicker from '../ui/CenteredTimePicker'
import MetricPreviewCard from '../ui/MetricPreviewCard'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'
import type { AppLanguage } from '../../i18n/languages'
import { isFemaleProfile } from '../../lib/cycle'
import type { ProfileStats } from '../../lib/profileStats'
import type { CycleProfile } from '../../lib/cycle'
import { formatWorkoutTimeDisplay } from '../../lib/trainingSchedule'

type AthleteConfigSheetProps = {
  open: boolean
  onClose: () => void
  stats: ProfileStats
  cycleProfile: CycleProfile | null
  language: AppLanguage
  onLanguageChange: (language: AppLanguage) => void
  conflictsLoading: boolean
  onOpenConflicts: () => void
  onSaveHeight: (value: number) => Promise<void>
  onSaveWeight: (value: number) => Promise<void>
  onSaveTrainingGoal: (value: number) => Promise<void>
  onSaveMealFrequency: (value: number) => Promise<void>
  onSaveBudget: (value: number) => Promise<void>
  onSaveTrainingTime: (value: string) => Promise<void>
  onSaveCycle: (fields: {
    last_period_start_date: string | null
    avg_cycle_length: number
    avg_period_duration: number
  }) => Promise<void>
}

type MetricId = 'height' | 'weight' | 'trainingGoal' | 'mealFrequency' | 'budget' | 'trainingTime'

type ConfigScreen =
  | { kind: 'main' }
  | { kind: 'metric'; id: MetricId }
  | { kind: 'cycle'; step: CyclePanelStep }

const AthleteConfigSheet = ({
  open,
  onClose,
  stats,
  cycleProfile,
  language,
  onLanguageChange,
  conflictsLoading,
  onOpenConflicts,
  onSaveHeight,
  onSaveWeight,
  onSaveTrainingGoal,
  onSaveMealFrequency,
  onSaveBudget,
  onSaveTrainingTime,
  onSaveCycle,
}: AthleteConfigSheetProps) => {
  const { t, i18n } = useTranslation()
  const [screen, setScreen] = useState<ConfigScreen>({ kind: 'main' })

  const metrics = useMemo(
    () =>
      [
        {
          id: 'height' as const,
          label: t('profile.height'),
          value: stats.height_cm,
          unit: 'cm',
          icon: 'height',
          min: 120,
          max: 230,
          onSave: onSaveHeight,
        },
        {
          id: 'weight' as const,
          label: t('profile.weight'),
          value: stats.current_weight_kg,
          unit: 'kg',
          icon: 'monitor_weight',
          min: 35,
          max: 200,
          step: 0.5,
          onSave: onSaveWeight,
        },
        {
          id: 'trainingTime' as const,
          label: t('profile.usualTrainingTime'),
          formattedValue: formatWorkoutTimeDisplay(stats.preferred_workout_start, i18n.language),
          icon: 'schedule',
        },
        {
          id: 'trainingGoal' as const,
          label: t('profile.trainingGoal'),
          value: stats.weekly_commitment,
          unit: t('profile.daysPerWeek'),
          icon: 'calendar_month',
          min: 2,
          max: 6,
          onSave: onSaveTrainingGoal,
        },
        {
          id: 'mealFrequency' as const,
          label: t('profile.mealFrequency'),
          value: stats.meal_frequency === '5_plus_meals' ? 5 : stats.meal_frequency === '4_meals' ? 4 : 3,
          unit: t('profile.mealsPerDay'),
          icon: 'restaurant',
          min: 3,
          max: 5,
          onSave: onSaveMealFrequency,
        },
        {
          id: 'budget' as const,
          label: t('profile.weeklyBudget'),
          value: stats.weekly_budget_value,
          unit: stats.preferred_currency ?? 'EUR',
          icon: 'payments',
          min: 20,
          max: 500,
          step: 5,
          onSave: onSaveBudget,
        },
      ] as const,
    [stats, t, i18n.language, onSaveHeight, onSaveWeight, onSaveTrainingGoal, onSaveMealFrequency, onSaveBudget],
  )

  const editingMetric = screen.kind === 'metric' ? metrics.find((m) => m.id === screen.id) : null

  const handleClose = () => {
    setScreen({ kind: 'main' })
    onClose()
  }

  const handleBack = () => {
    if (screen.kind === 'metric') {
      setScreen({ kind: 'main' })
      return
    }
    if (screen.kind === 'cycle' && screen.step !== 'overview') {
      setScreen({ kind: 'cycle', step: 'overview' })
      return
    }
    if (screen.kind === 'cycle') {
      setScreen({ kind: 'main' })
    }
  }

  const panelTitle = (() => {
    if (screen.kind === 'metric' && editingMetric) return editingMetric.label
    if (screen.kind === 'cycle') {
      if (screen.step === 'cycleLength') return t('app.profile.cycleLength')
      if (screen.step === 'periodDays') return t('app.profile.periodDays')
      return t('app.profile.cycleTracking')
    }
    return t('profile.athleteConfig')
  })()

  const showBack = screen.kind !== 'main'

  return (
    <SlidePagePanel
      open={open}
      onClose={handleClose}
      onBack={showBack ? handleBack : undefined}
      title={panelTitle}
    >
      {screen.kind === 'metric' && screen.id === 'trainingTime' ? (
        <CenteredTimePicker
          label={t('profile.usualTrainingTime')}
          value={stats.preferred_workout_start}
          onSave={async (value) => {
            await onSaveTrainingTime(value)
            setScreen({ kind: 'main' })
          }}
        />
      ) : screen.kind === 'metric' && editingMetric && 'onSave' in editingMetric ? (
        <CenteredMetricStepper
          label={editingMetric.label}
          value={editingMetric.value}
          unit={editingMetric.unit}
          icon={editingMetric.icon}
          min={editingMetric.min}
          max={editingMetric.max}
          step={'step' in editingMetric ? editingMetric.step : 1}
          onSave={async (value) => {
            await editingMetric.onSave(value)
            setScreen({ kind: 'main' })
          }}
        />
      ) : screen.kind === 'cycle' && cycleProfile ? (
        <CycleTrackingPanel
          profile={cycleProfile}
          step={screen.step}
          onStepChange={(step) => setScreen({ kind: 'cycle', step })}
          onSave={onSaveCycle}
        />
      ) : (
        <div className="space-y-5">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <MaterialIcon name="language" hoverable size="sm" />
              <p className={KILO.meta}>{t('profile.language')}</p>
            </div>
            <LanguagePicker value={language} onChange={(next) => void onLanguageChange(next)} />
          </div>

          <div className="grid grid-cols-2 items-stretch gap-2">
            {metrics.map((metric) => (
              <MetricPreviewCard
                key={metric.id}
                label={metric.label}
                value={'value' in metric ? metric.value : undefined}
                unit={'unit' in metric ? metric.unit : undefined}
                formattedValue={'formattedValue' in metric ? metric.formattedValue : undefined}
                icon={metric.icon}
                onClick={() => setScreen({ kind: 'metric', id: metric.id })}
              />
            ))}

            {stats.pending_conflicts > 0 ? (
              <button
                type="button"
                className="rounded-xl border border-amber-900/40 bg-amber-950/30 p-3 text-left"
                onClick={onOpenConflicts}
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-600">{t('profile.conflicts')}</p>
                <p className="mt-1 text-sm font-bold text-amber-400">
                  {conflictsLoading ? t('profile.conflictsLoading') : t('profile.conflictsPending', { count: stats.pending_conflicts })}
                </p>
                <p className="mt-1 text-xs text-amber-200/80">{t('profile.conflictsManage')}</p>
              </button>
            ) : null}
          </div>

          {isFemaleProfile(stats.sex) && cycleProfile ? (
            <CycleTrackingTeaser profile={cycleProfile} onOpen={() => setScreen({ kind: 'cycle', step: 'overview' })} />
          ) : null}
        </div>
      )}
    </SlidePagePanel>
  )
}

export default AthleteConfigSheet
